import React, { useEffect, useMemo, useState } from 'react';
import { diffJson } from 'diff';
import { Button } from 'react-bootstrap';
import { ChevronUp, ChevronDown } from 'react-bootstrap-icons';
import ContextToggle from './ContextToggle';
import ToolsView from './ToolsView';

function scrollToDiffIndex(n, totalChanges, setActiveDiff, currentActive) {
  let target = n;
  if (target < 0) target = (totalChanges - 1) || 0;
  const rows = document.querySelectorAll('.diff-result div:not(.no_change)');
  const firstDiff = rows[target];
  if (firstDiff) {
    const y = firstDiff.offsetTop;
    const diffResult = document.querySelectorAll('.diff-result')[0];
    try {
      diffResult.scrollTop = y - 400;
      if (currentActive !== target) setActiveDiff(target);
    } catch (err) { /* noop */ }
  }
}

export default function DiffView({ source, target, showContext, folder, callDVS }) {
  const [activeDiff, setActiveDiff] = useState(0);

  const diff = useMemo(() => {
    if (source && target && source.length > 0 && target.length > 0) {
      return diffJson(source, target);
    }
    return [];
  }, [source, target]);

  const diffChanges = useMemo(
    () => diff.filter((part) => part.added || part.removed),
    [diff],
  );

  // After diff (re)computes, scroll to the first change in the next paint.
  useEffect(() => {
    if (diff.length > 0 && diffChanges.length > 0) {
      // Defer to next frame so the DOM nodes exist.
      const id = window.requestAnimationFrame(() => {
        scrollToDiffIndex(0, diffChanges.length, setActiveDiff, activeDiff);
      });
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
    // intentionally only on diff
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diff]);

  if (diff.length === 1) return (<h3 className="no-diff">No Difference</h3>);
  if (diff.length === 0) return null;

  const result = diff.map((part, index) => {
    const addClass = part.added ? 'added' : part.removed ? 'removed' : 'no_change';
    return <div key={index} className={addClass}>{part.value}</div>;
  });

  const diffResultClasses = 'diff-result' + (!showContext ? ' no-context' : '');

  return (
    <pre className={diffResultClasses}>
      {result}
      <div className={showContext ? 'change-navigator' : 'hide'}>
        <Button
          className={diffChanges.length <= 1 ? 'hide' : 'prev-diff-btn'}
          variant="primary"
          onClick={() => scrollToDiffIndex(
            (activeDiff - 1) % diffChanges.length,
            diffChanges.length,
            setActiveDiff,
            activeDiff,
          )}
        >
          <ChevronUp />
        </Button>
        <div className="change-navigator-text">
          <h4>{diffChanges.length + ' Change' + (diffChanges.length === 1 ? '' : 's')}</h4>
          <h5 className={diffChanges.length <= 1 ? 'hide' : ''}>
            {'Showing ' + (activeDiff + 1) + ' of ' + diffChanges.length}
          </h5>
        </div>
        <Button
          className={diffChanges.length <= 1 ? 'hide' : 'next-diff-btn'}
          variant="primary"
          onClick={() => scrollToDiffIndex(
            (activeDiff + 1) % diffChanges.length,
            diffChanges.length,
            setActiveDiff,
            activeDiff,
          )}
        >
          <ChevronDown />
        </Button>
      </div>
      {folder === 'config' && (
        <ToolsView diff={diff} callDVS={callDVS} />
      )}
      <ContextToggle />
    </pre>
  );
}
